"use client"

import React, { useState } from "react"

import Link from "next/link"
import Image from "next/image"
import { Caveat } from "next/font/google"

import docxster_logo from "public/images/svgs/docxster-logo-with-name.svg"
import { landingPageSvgs } from "public/assets/landingPageSvgs"
import FormButton from "components/form/FormBtn/FormButton"
import FormInput from "components/form/FormInput/input"
import { toastAlert } from "components/Toast/ToastNotification"

const caveat = Caveat({
  subsets: ["latin"],
  weight: ["400"],
  variable: "--font-caveat",
})

interface AnimatedButtonProps {
  label: string
  onClick?: () => void
}

const AnimatedButton = ({ label, onClick }: AnimatedButtonProps) => {
  return (
    <button className="group cursor-pointer py-1 font-medium hover:text-[#3F3F3F]" onClick={onClick}>
      <div className="relative overflow-hidden">
        <p className="ease-&lsqb;cubic-bezier(0.19,1,0.22,1)&rsqb; whitespace-nowrap duration-500 group-hover:-translate-y-7">
          {label}
        </p>
        <p className="ease-&lsqb;cubic-bezier(0.19,1,0.22,1)&rsqb; absolute left-0 top-7 whitespace-nowrap underline-offset-2 duration-700 hover:underline group-hover:top-0">
          {label}
        </p>
      </div>
    </button>
  )
}

const FooterSection = () => {
  const [email, setEmail] = useState("")

  const platformLinks = [
    { label: "Invoice Processing" },
    { label: "Purchase Order Automation" },
    { label: "Driving License Verification" },
    { label: "Passport Verification" },
    { label: "Passport Data Extraction" },
    { label: "Bank Statement Processing" },
    { label: "Bill of Lading Management" },
    { label: "Contract Summarization" },
  ]

  const toolsLinks = [
    { label: "PDF to Word" },
    { label: "JPG to PDF" },
    { label: "Extract Data" },
    { label: "Docxster Drive" },
    { label: "IDP" },
    { label: "Workflow Builder" },
    { label: "Automation Platform" },
    { label: "Validation Platform" },
  ]

  const companyLinks = [
    { label: "About Docxster" },
    { label: "Careers" },
    { label: "Pricing", link: "/pricing" },
    { label: "Schedule a Demo", link: "/demo" },
  ]

  const resourcesLinks = [
    { label: "Integrations" },
    { label: "Templates" },
    { label: "Blog", link: "/blog" },
    { label: "Why Docxster", link: "/comparison" },
  ]

  const socialLinks = [
    // { icon: "facebookIcon", link: "" },
    { icon: "xIcon", link: "https://x.com/docxsterHQ" },
    { icon: "linkedinIcon", link: "https://www.linkedin.com/company/docxster" },
    { icon: "integrationIcon", link: "https://docxster.com" },
  ]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setEmail(value)
  }

  const handleSubscribe = () => {
    if (!email) {
      toastAlert.error("Email can't be blank", "Please enter your email")
    } else {
      console.log("Subscribed")
    }
  }

  return (
    <div className="relative bg-[#F5F5F5] p-6 py-20 md:p-12 lg:p-20 3xl:px-40">
      <div>
        <div className="relative flex">
          <p className="mb-3 text-[28px] font-bold leading-[120%] tracking-[-1.2px] text-[#3F3F3F] md:text-[32px] lg:text-[40px]">
            Get Document Intelligence
            <span className="font-medium text-[#3F3F3FBF]"> in Your Inbox.</span>
          </p>
          <p className="mt-2 hidden lg:block ">
            {landingPageSvgs["circularArrowIcon"]?.({ fill: "#3f3f3f3d", height: 18, width: 85 })}
          </p>
          <p
            className={`${caveat.className} caveat-font -ml-4 mt-4 hidden rotate-[3.378deg] text-[32px] font-medium leading-[150%] text-[#3F3F3F]  lg:block`}
          >
            sent just once a week.
          </p>
        </div>
        <p className="self-stretch text-xs font-medium leading-[150%] tracking-[-0.44px] text-[#3F3F3FBF]  md:text-base lg:text-xl">
          Actionable tips, automation trends, and exclusive product updates.
        </p>

        <div className="mt-4 flex flex-col gap-3 md:flex-row lg:mt-8 lg:gap-6">
          <FormInput
            type="email"
            handler={handleChange}
            name="email"
            value={email}
            placeHolder="Enter your email"
            customClass="focus:shadow-outline h-9 md:h-12 px-6 rounded border bg-white px-4 md:min-w-[383px] text-sm md:text-base font-normal focus:outline-none"
          />

          <FormButton
            handler={handleSubscribe}
            label="Subscribe"
            customClass="w-[200px] h-12 text-base font-semibold py-[5px] shrink-0 rounded bg-[#1E1E1E] transition-shadow shadow-[-62px_67px_26px_0px_#00000000,-40px_43px_23px_0px_#00000008,-22px_24px_20px_0px_#00000019,-10px_11px_15px_0px_#00000029,-2px_3px_8px_0px_#00000030] hover:shadow-none"
          />
        </div>
      </div>

      <div className="mb-[64px] mt-20 flex w-full gap-6 md:gap-56 lg:justify-between lg:gap-10 2xl:gap-20">
        <div className="flex flex-col gap-12 lg:min-w-[40%] lg:flex-row lg:justify-between ">
          <div className="flex flex-col  gap-3">
            <h1 className="self-stretch text-base font-bold leading-[150%] tracking-[-0.304px] text-[#3F3F3F]">
              PLATFORM
            </h1>
            {platformLinks.map((item, idx) => (
              <div
                key={idx}
                className=" self-stretch text-xs font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3FBF]  md:text-base"
              >
                <AnimatedButton label={item.label} />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="self-stretch text-base font-bold leading-[150%] tracking-[-0.304px] text-[#3F3F3F]">
              RESOURCES
            </h1>
            {resourcesLinks.map((item, idx) => (
              <div
                key={idx}
                className="self-stretch text-xs font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3FBF]  md:text-base"
              >
                {/* remove conditional rendering after adding all links to resourcesLinks */}
                {item.link ? (
                  <Link href={item.link}>
                    <AnimatedButton label={item.label} />
                  </Link>
                ) : (
                  <AnimatedButton label={item.label} />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-12 lg:min-w-[40%] lg:flex-row lg:justify-between ">
          <div className="flex flex-col gap-3">
            <h1 className="self-stretch text-base font-bold leading-[150%] tracking-[-0.304px] text-[#3F3F3F]">
              TOOLS
            </h1>
            {toolsLinks.map((item, idx) => (
              <div
                key={idx}
                className="self-stretch text-xs font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3FBF]  md:text-base"
              >
                <AnimatedButton label={item.label} />
              </div>
            ))}
          </div>
          <div className="flex flex-col  gap-3">
            <h1 className="self-stretch text-base font-bold leading-[150%] tracking-[-0.304px] text-[#3F3F3F]">
              COMPANY
            </h1>
            {companyLinks.map((item, idx) => (
              <div
                key={idx}
                className=" self-stretch text-xs font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3FBF]  md:text-base"
              >
                {/* remove conditional rendering after adding all links to companyLinks */}
                {item.link ? (
                  <Link href={item.link}>
                    <AnimatedButton label={item.label} />
                  </Link>
                ) : (
                  <AnimatedButton label={item.label} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="hidden w-full justify-center md:flex">
        {landingPageSvgs["footerDashedLineIcon"]?.({ fill: "#3f3f3f3d", height: 2, width: 1280 })}
      </p>

      <div className="mt-[50px] hidden flex-col md:flex">
        <div className="-mb-5 flex items-center justify-between">
          <Link href="/" className="-ml-9 cursor-pointer">
            <Image src={docxster_logo} alt="docxster-logo" />
          </Link>
          <div className="flex gap-6">
                   {socialLinks.map((item, idx) => (
              <Link href={item.link} key={idx} className="cursor-pointer">
                {landingPageSvgs[item.icon]?.({ fill: "#3F3F3FAF", height: 24, width: 24 })}
              </Link>
            ))}
          </div>
        </div>
        <div className="flex justify-between">
          <p className="text-base font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3F] text-opacity-75">
            © 2025 Docxster.ai | All rights reserved.
          </p>
          <Link
            href="/privacy"
            className="self-stretch text-right text-base font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3F] text-opacity-75 underline decoration-solid  underline-offset-2"
          >
            Privacy policy
          </Link>
        </div>
      </div>

      <div className="mt-6  flex-col md:hidden">
        <div className="-mb-5 items-center justify-between">
          <Link href="/" className="max-w-[180px] cursor-pointer">
            <Image src={docxster_logo} alt="docxster-logo" />
          </Link>
        </div>
        <div className="flex flex-col justify-center gap-6">
          <div className="flex justify-center gap-6">
            {socialLinks.map((item, idx) => (
              <Link href={item.link} key={idx} className="cursor-pointer">
                {landingPageSvgs[item.icon]?.({ fill: "#3F3F3FAF", height: 24, width: 24 })}
              </Link>
            ))}
          </div>
          <Link
            href="/privacy"
            className="self-stretch text-center text-base font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3FBF]  underline decoration-solid  underline-offset-2"
          >
            Privacy policy
          </Link>
          <p className="text-center text-base font-medium leading-[150%] tracking-[-0.352px] text-[#3F3F3FBF] ">
            © 2025 Docxster.ai | All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}

export default FooterSection
